import { logger } from './logger';
import { formatUserDisplayName } from '../lib/utils';

// Types
export interface UserDetails {
  id: string;
  email: string;
  display_name?: string;
  avatar_url?: string;
  last_fetched?: number;
}

// In-memory cache for user data
type UserCache = {
  [userId: string]: UserDetails;
};

// Global cache instance (singleton)
const userCache: UserCache = {};

// Constants
const CACHE_TTL = 10 * 60 * 1000; // 10 minutes in milliseconds

/**
 * Add or update a user in the cache
 * @param userId The user ID
 * @param details The user details to cache
 */
export const cacheUser = (userId: string, details: Omit<UserDetails, 'last_fetched'>): void => {
  if (!userId) return;

  // Format the display name if it exists
  const formattedDetails = {
    ...details,
    display_name: details.display_name ? formatUserDisplayName(details.display_name) : details.display_name
  };

  userCache[userId] = {
    ...formattedDetails,
    last_fetched: Date.now(),
  };

  // If there's an avatar URL, preload it for faster display
  if (details.avatar_url) {
    preloadImage(details.avatar_url);
  }
};

/**
 * Get a user from the cache if available and not expired
 * @param userId The user ID to retrieve
 * @returns The cached user details or null if not found or expired
 */
export const getCachedUser = (userId: string): UserDetails | null => {
  if (!userId || !userCache[userId]) return null;

  const user = userCache[userId];
  const now = Date.now();

  // Check if the cached data is still valid
  if (user.last_fetched && now - user.last_fetched < CACHE_TTL) {
    return user;
  }

  return null;
};

/**
 * Check if a user exists in the cache and is not expired
 * @param userId The user ID to check
 * @returns True if the user is in the cache and not expired
 */
export const isUserCached = (userId: string): boolean => {
  return getCachedUser(userId) !== null;
};

/**
 * Clear the entire user cache
 */
export const clearUserCache = (): void => {
  Object.keys(userCache).forEach(key => {
    delete userCache[key];
  });
  logger.store('User cache cleared');
};

/**
 * Update the cache with multiple users at once
 * @param users An array of user details to cache
 */
export const cacheUsers = (
  users: Array<{ id: string } & Omit<UserDetails, 'id' | 'last_fetched'>>
): void => {
  users.forEach(user => {
    if (user.id) {
      // Use cacheUser to ensure consistent formatting
      cacheUser(user.id, user);
    }
  });
};

/**
 * Preload an image to browser cache
 * @param url The image URL to preload
 */
const preloadImage = (url: string): void => {
  if (!url) return;

  // Create a new image element and set the source
  const img = new Image();
  img.src = url;

  // We don't need to add this to the DOM or track it
  // The browser will cache it just by loading the URL
};

/**
 * Get a display name for a user, with fallbacks
 * @param user User details object
 * @returns Best available display name
 */
export const getDisplayName = (user: UserDetails | null | undefined): string => {
  if (!user) return 'Unknown User';

  // Format display name with proper capitalization if available
  if (user.display_name) {
    return formatUserDisplayName(user.display_name);
  }

  // Fall back to email username or full email
  return (user.email ? user.email.split('@')[0] : null) || user.email || 'Unknown User';
};

/**
 * Utility for generating consistent avatar initials from a name
 * @param name The name to get initials from
 * @returns The initials (usually 1-2 characters)
 */
export const getInitials = (name: string): string => {
  if (!name) return '?';

  // Extract first character of each word, max 2 characters
  const initials = name
    .split(/\s+/)
    .map(word => word.charAt(0).toUpperCase())
    .filter(Boolean)
    .slice(0, 2)
    .join('');

  return initials || name.charAt(0).toUpperCase();
};
